/**
 * @module i18n/types
 * @description Type definitions for internationalization
 */

// =============================================================================
// LOCALE TYPES
// =============================================================================

/** Supported locales */
export type Locale = 'en' | 'fr';

/** Default locale */
export const DEFAULT_LOCALE: Locale = 'en';

/** Available locales */
export const AVAILABLE_LOCALES: Locale[] = ['en', 'fr'];

/** Locale display names */
export const LOCALE_NAMES: Record<Locale, string> = {
  en: 'English',
  fr: 'Français',
};

// =============================================================================
// TRANSLATION STRUCTURE
// =============================================================================

/** Common translations (shared across the app) */
export interface CommonTranslations {
  appName: string;
  loading: string;
  error: string;
  success: string;
  warning: string;
  info: string;
  save: string;
  cancel: string;
  delete: string;
  edit: string;
  create: string;
  creating: string;
  close: string;
  done: string;
  confirm: string;
  yes: string;
  no: string;
  ok: string;
  back: string;
  next: string;
  search: string;
  clear: string;
  refresh: string;
  copy: string;
  duplicate: string;
  export: string;
  import: string;
  settings: string;
  help: string;
  logout: string;
  noResults: string;
  required: string;
  optional: string;
  retry: string;
}

/** Auth-related translations */
export interface AuthTranslations {
  title: string;
  apiKeyLabel: string;
  apiKeyPlaceholder: string;
  apiKeyHelp: string;
  apiKeyInvalid: string;
  login: string;
  loggingIn: string;
  loginSuccess: string;
  loginFailed: string;
  logoutSuccess: string;
  selectLibrary: string;
  selectLibraryHelp: string;
  myLibrary: string;
  groupLibraries: string;
  noGroups: string;
}

/** Library-related translations */
export interface LibraryTranslations {
  title: string;
  myLibrary: string;
  groups: string;
  noGroups: string;
  groupLibraries: string;
  selectLibrary: string;
  librarySelected: string;
  loadingLibraries: string;
  loadingGroups: string;
  errorLoadingGroups: string;
}

/** Collection-related translations */
export interface CollectionTranslations {
  title: string;
  collections: string;
  allItems: string;
  unfiled: string;
  trash: string;
  duplicates: string;
  publications: string;
  newCollection: string;
  newSubcollection: string;
  renameCollection: string;
  deleteCollection: string;
  collectionName: string;
  collectionNamePlaceholder: string;
  collectionCreated: string;
  collectionRenamed: string;
  collectionDeleted: string;
  collectionEmpty: string;
  confirmDelete: string;
  confirmDeleteMessage: string;
  expandAll: string;
  collapseAll: string;
  noCollections: string;
  loadingCollections: string;
  errorLoadingCollections: string;
  emptyTrash: string;
  emptyTrashConfirm: string;
  trashEmptied: string;
}

/** Item field labels */
export interface ItemFieldTranslations {
  title: string;
  creators: string;
  date: string;
  originalDate: string;
  publisher: string;
  place: string;
  language: string;
  abstractNote: string;
  url: string;
  extra: string;
  accessDate: string;
  rights: string;
  shortTitle: string;
  ISBN: string;
  edition: string;
  volume: string;
  series: string;
  seriesTitle: string;
  seriesText: string;
  numPages: string;
  numberOfVolumes: string;
  seriesNumber: string;
  bookTitle: string;
  pages: string;
  publicationTitle: string;
  issue: string;
  DOI: string;
  ISSN: string;
  journalAbbreviation: string;
  section: string;
  proceedingsTitle: string;
  conferenceName: string;
  encyclopediaTitle: string;
  dictionaryTitle: string;
  websiteTitle: string;
  websiteType: string;
  blogTitle: string;
  forumTitle: string;
  postType: string;
  university: string;
  thesisType: string;
  manuscriptType: string;
  letterType: string;
  subject: string;
  archive: string;
  archiveLocation: string;
  libraryCatalog: string;
  callNumber: string;
  medium: string;
  interviewMedium: string;
  artworkMedium: string;
  artworkSize: string;
  mapType: string;
  scale: string;
  studio: string;
  label: string;
  distributor: string;
  genre: string;
  runningTime: string;
  videoRecordingFormat: string;
  audioRecordingFormat: string;
  audioFileType: string;
  programTitle: string;
  network: string;
  episodeNumber: string;
  presentationType: string;
  meetingName: string;
  institution: string;
  reportNumber: string;
  reportType: string;
  // Software fields
  versionNumber: string;
  system: string;
  company: string;
  programmingLanguage: string;
  // Dataset fields
  identifier: string;
  repository: string;
  repositoryLocation: string;
  format: string;
  citationKey: string;
  // Standard fields
  number: string;
  organization: string;
  committee: string;
  type: string;
  status: string;
  // Legal: Legislation
  nameOfAct: string;
  billNumber: string;
  code: string;
  codeVolume: string;
  codeNumber: string;
  publicLawNumber: string;
  dateEnacted: string;
  codePages: string;
  legislativeBody: string;
  session: string;
  history: string;
  documentNumber: string;
  // Legal: Cases
  caseName: string;
  court: string;
  dateDecided: string;
  docketNumber: string;
  reporter: string;
  reporterVolume: string;
  firstPage: string;
  // Legal: Patents
  country: string;
  assignee: string;
  issuingAuthority: string;
  patentNumber: string;
  filingDate: string;
  issueDate: string;
  applicationNumber: string;
  priorityNumbers: string;
  references: string;
  legalStatus: string;
  // Other
  originalTitle: string;
  originalPublisher: string;
  originalPublisherPlace: string;
  PMID: string;
  PMCID: string;
}

/** Item-related translations */
export interface ItemTranslations {
  title: string;
  items: string;
  item: string;
  newItem: string;
  selectType: string;
  editItem: string;
  deleteItem: string;
  deleteItems: string;
  duplicateItem: string;
  itemCreated: string;
  itemUpdated: string;
  itemDeleted: string;
  itemsDeleted: string;
  itemDuplicated: string;
  confirmDelete: string;
  confirmDeleteMessage: string;
  confirmDeleteMultiple: string;
  noItems: string;
  loadingItems: string;
  errorLoadingItems: string;
  // Item fields (basic)
  itemType: string;
  title_field: string;
  creators: string;
  date: string;
  dateAdded: string;
  dateModified: string;
  abstract: string;
  publisher: string;
  publicationTitle: string;
  volume: string;
  issue: string;
  pages: string;
  doi: string;
  isbn: string;
  issn: string;
  url: string;
  accessDate: string;
  language: string;
  tags: string;
  notes: string;
  attachments: string;
  // Creator management
  addCreator: string;
  removeCreator: string;
  firstName: string;
  lastName: string;
  // All field labels
  fields: ItemFieldTranslations;
  // Item types
  types: {
    book: string;
    bookSection: string;
    journalArticle: string;
    magazineArticle: string;
    newspaperArticle: string;
    thesis: string;
    report: string;
    conferencePaper: string;
    webpage: string;
    document: string;
    presentation: string;
    patent: string;
    statute: string;
    case: string;
    interview: string;
    film: string;
    artwork: string;
    podcast: string;
    videoRecording: string;
    audioRecording: string;
    encyclopediaArticle: string;
    dictionaryEntry: string;
    manuscript: string;
    radioBroadcast: string;
    tvBroadcast: string;
    blogPost: string;
    forumPost: string;
    letter: string;
    email: string;
    instantMessage: string;
    map: string;
    dataset: string;
    standard: string;
    computerProgram: string;
    bill: string;
    hearing: string;
  };
  // Creator types
  creatorTypes: {
    author: string;
    editor: string;
    translator: string;
    contributor: string;
    bookAuthor: string;
    seriesEditor: string;
    reviewedAuthor: string;
    director: string;
    artist: string;
    performer: string;
    composer: string;
    interviewer: string;
    interviewee: string;
    producer: string;
    presenter: string;
    scriptwriter: string;
    wordsBy: string;
    castMember: string;
    guest: string;
    podcaster: string;
    cartographer: string;
    programmer: string;
    recipient: string;
    commenter: string;
    sponsor: string;
    cosponsor: string;
    counsel: string;
    inventor: string;
    attorneyAgent: string;
  };
}

/** Search-related translations */
export interface SearchTranslations {
  placeholder: string;
  searching: string;
  noResults: string;
  resultsCount: string;
  recentSearches: string;
  clearHistory: string;
  searchIn: string;
  searchAll: string;
  searchTitles: string;
  searchCreators: string;
  searchTags: string;
}

/** Sort-related translations */
export interface SortTranslations {
  sortBy: string;
  ascending: string;
  descending: string;
  title: string;
  creator: string;
  date: string;
  dateAdded: string;
  dateModified: string;
  itemType: string;
  year: string;
  publisher: string;
}

/** Pagination translations */
export interface PaginationTranslations {
  page: string;
  of: string;
  items: string;
  showing: string;
  first: string;
  previous: string;
  next: string;
  last: string;
  perPage: string;
}

/** Export-related translations */
export interface ExportTranslations {
  title: string;
  format: string;
  bibtex: string;
  biblatex: string;
  ris: string;
  csljson: string;
  mods: string;
  refer: string;
  tei: string;
  wikipedia: string;
  csv: string;
  exportSuccess: string;
  exportFailed: string;
  copyToClipboard: string;
  copiedToClipboard: string;
  copyFailed: string;
  copyTitle: string;
  copyUrl: string;
  copyCitation: string;
  // Export modal keys
  exportItems: string;
  exportItem: string;
  exportCollection: string;
  selectedItems: string;
  formatLabel: string;
  styleLabel: string;
  bibliography: string;
  download: string;
  preview: string;
  generating: string;
  noItemsSelected: string;
  selectItemsFirst: string;
}

/** Details panel translations */
export interface DetailsPanelTranslations {
  title: string;
  noItemSelected: string;
  selectItemToView: string;
  viewMode: string;
  editMode: string;
  saveChanges: string;
  discardChanges: string;
  unsavedChanges: string;
  confirmDiscard: string;
  info: string;
  notes: string;
  tags: string;
  related: string;
}

/** Error messages */
export interface ErrorTranslations {
  generic: string;
  network: string;
  unauthorized: string;
  forbidden: string;
  notFound: string;
  serverError: string;
  timeout: string;
  rateLimited: string;
  invalidApiKey: string;
  loadFailed: string;
  saveFailed: string;
  deleteFailed: string;
  connectionLost: string;
  tryAgain: string;
}

/** Settings translations */
export interface SettingsTranslations {
  title: string;
  language: string;
  theme: string;
  darkMode: string;
  lightMode: string;
  autoTheme: string;
  panelSizes: string;
  resetPanels: string;
  clearCache: string;
  clearCacheConfirm: string;
  cacheCleared: string;
  about: string;
  version: string;
  // Item types & fields preferences
  itemTypes: string;
  fields: string;
  itemTypesDescription: string;
  fieldsDescription: string;
  selectItemType: string;
  selectAll: string;
  selectCommon: string;
  showAllFields: string;
  showEssential: string;
  resetToDefault: string;
  resetAll: string;
  confirmReset: string;
  preferencesReset: string;
}

/** Accessibility translations */
export interface A11yTranslations {
  skipToContent: string;
  mainNavigation: string;
  collectionsPanel: string;
  itemsPanel: string;
  detailsPanel: string;
  closeModal: string;
  expandCollection: string;
  collapseCollection: string;
  selectItem: string;
  openMenu: string;
  closeMenu: string;
  loading: string;
  sortAscending: string;
  sortDescending: string;
  itemActions: string;
  collectionActions: string;
}

/** Scan modal translations */
export interface ScanTranslations {
  title: string;
  scanButton: string;
  scanning: string;
  hint: string;
  scannedISBN: string;
  scanAnother: string;
  cameraAccessDenied: string;
  noCameraFound: string;
  unsupportedBrowser: string;
  // ISBN search
  searchingCatalogs: string;
  foundIn: string;
  notFound: string;
  searchedIn: string;
}

/** Import modal translations */
export interface ImportTranslations {
  // General
  title: string;
  importButton: string;
  // Tabs
  tabCatalogs: string;
  tabFile: string;
  // Catalog search
  searchPlaceholder: string;
  searchIndex: string;
  documentType: string;
  searching: string;
  noResults: string;
  tryDifferentSearch: string;
  welcomeMessage: string;
  searchHint: string;
  enterSearchTerm: string;
  searchFailed: string;
  resultsCount: string;
  selectAll: string;
  untitled: string;
  selectRecordsFirst: string;
  noLibrarySelected: string;
  importing: string;
  importSelected: string;
  importSuccess: string;
  importPartialFailed: string;
  // Source selection
  source: string;
  sourceSudoc: string;
  sourceBnf: string;
  sourceGoogleBooks: string;
  sourceSwisscovery: string;
  sourceOpenLibrary: string;
  sourceHarvardLibrary: string;
  sourceWikidata: string;
  sudocSearchHint: string;
  googleBooksSearchHint: string;
  harvardLibrarySearchHint: string;
  bnfSearchHint: string;
  swisscoverySearchHint: string;
  openLibrarySearchHint: string;
  wikidataSearchHint: string;
  addCriteria: string;
  // File import
  dropZoneText: string;
  dropZoneHint: string;
  browseFiles: string;
  supportedFormats: string;
  detectedFormat: string;
  fileParseSuccess: string;
  fileParseFailed: string;
  recordsFound: string;
}

// =============================================================================
// COMPLETE TRANSLATIONS TYPE
// =============================================================================

/** Complete translations object */
export interface Translations {
  common: CommonTranslations;
  auth: AuthTranslations;
  library: LibraryTranslations;
  collection: CollectionTranslations;
  item: ItemTranslations;
  search: SearchTranslations;
  sort: SortTranslations;
  pagination: PaginationTranslations;
  export: ExportTranslations;
  detailsPanel: DetailsPanelTranslations;
  errors: ErrorTranslations;
  settings: SettingsTranslations;
  a11y: A11yTranslations;
  scan: ScanTranslations;
  import: ImportTranslations;
}

// =============================================================================
// TRANSLATION HELPERS
// =============================================================================

/** Translation key path (dot notation) */
export type TranslationKey = string;

/** Interpolation values for translations */
export type InterpolationValues = Record<string, string | number>;
